import React from "react";
import { useGetSingleUserPermissions } from "../../../../hooks/useGetSingleUserPermissions";
import { useParams } from "react-router-dom";
import * as mui from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { PERMISSIONS_EDIT } from "../../../../enums/QueryKeys";
import Skeletons from "../../../../components/loader/Skeletons";
import ErrorFullScreenMessage from "../../../../components/fullScreenMessage/ErrorFullScreenMessage";
import MyLoadingButton from "../../../../components/buttons/MyLoadingButton";
import EmployeeEditForm from "./form/EmployeeEditForm";
import { useEmployeeValidation } from "../../../profile/form/Validation/UserInformationValidation";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { replaceEmptyStringsWithNull } from "../../../../utils/Functions";
import { EDIT_PROFILE } from "../../../../enums/ApiPaths";
import { zodResolver } from "@hookform/resolvers/zod";
import PageContentWrapper from "../../../../components/wrappers/PageContentWrapper";
import GoBack from "../../../../components/buttons/GoBack";
import usePermissionPut from "../hooks/usePermissionPut";
import { useRedirectIfNotOwnClinic } from "../../../../hooks/useRedirectIfNotOwnClinic";

export default function EmployeeEdit() {
  const { id } = useParams();
  const { data, loading, error } = useGetSingleUserPermissions(id);
  const employee = data?.user;
  const userProfile = employee?.userDetails;
  useRedirectIfNotOwnClinic(employee?.clinic?.id, loading || !data);
  const { t } = useTranslation();
  const put = usePermissionPut(EDIT_PROFILE + id, PERMISSIONS_EDIT + id);

  const methods = useForm({
    resolver: zodResolver(useEmployeeValidation(userProfile?.phoneNumber)),
  });

  React.useEffect(() => {
    if (userProfile?.subscribeToEmails !== undefined) {
      methods.setValue("subscribeToEmails", userProfile?.subscribeToEmails);
    }
  }, [userProfile]);

  React.useEffect(() => {
    if (userProfile?.birthday) {
      methods.setValue("dob", dayjs(userProfile?.birthday, "DD/MM/YYYY"));
    }
    if (userProfile?.location) {
      // Never put null into the country Select - "" is its empty value
      methods.setValue("country", userProfile?.location?.country ?? "");
    }
    if (userProfile?.subscribeToEmails) {
      methods.setValue(
        "subscribeToEmails",
        Boolean(userProfile?.subscribeToEmails)
      );
    }
  }, [userProfile]);

  const submitData = async (e) => {
    e.email = userProfile?.email;

    const formattedBirthday =
      e?.dob && dayjs(e.dob).isValid()
        ? dayjs(e.dob).format("DD/MM/YYYY")
        : null;

    const data = {
      userDetails: {
        ...e,
        birthday: formattedBirthday,
        subscribeToEmails: e.subscribeToEmails,
        location: {
          address: e.address,
          place: e.place,
          province: e.province,
          zipcode: e.zipcode,
          city: e.city,
          country: e.country,
        },
      },
      clinic: employee?.clinic,
    };

    const filtered = replaceEmptyStringsWithNull(data);
    put.submitCallback(filtered);
  };

  if (loading) {
    return <Skeletons numOfSkeletons={12} grid={false} height={50} />;
  } else if (error) {
    return <ErrorFullScreenMessage />;
  }

  return (
    <form onSubmit={methods.handleSubmit(submitData)}>
      <PageContentWrapper>
        <FormProvider {...methods}>
          <mui.Paper
            sx={{
              p: 1,
              m: 1,
              mb: 5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <GoBack />
            <mui.Typography variant="h5">
              {employee?.userDetails?.firstName +
                " " +
                employee?.userDetails?.lastName}
            </mui.Typography>
            <MyLoadingButton
              size="small"
              isLoading={put.loading}
              message={t("Save")}
            />
          </mui.Paper>
          <mui.Stack spacing={2}>
            <EmployeeEditForm />
          </mui.Stack>
        </FormProvider>
      </PageContentWrapper>
    </form>
  );
}
